import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminCountdownTimer from '../components/AdminCountdownTimer';
import { ArrowLeft, BookOpen, Target, Heart, Quote, Phone, Mail, Users, Lightbulb, Sprout, Building2, User, Monitor, GraduationCap, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

// Organization personnel data with photos
const orgPersonnel = {
  moderator: {
    role: "Moderator",
    person: "Rev. Justice Hagan",
    photo: "https://customer-assets.emergentagent.com/job_72ece9e7-e94e-4944-ab3d-b8ae683d264f/artifacts/6ethajol_bce7730c-1a66-4e51-aeaf-073a98c1fcc9.jpeg"
  },
  administrator: {
    role: "Administrator", 
    person: "ES Ivanna"
  },
  teachers: {
    role: "Teachers",
    persons: [
      { name: "LP Elaine Osei", photo: null },
      { name: "LP Yvonne Fleischer", photo: null },
      { name: "LP Emma Mefful", photo: null },
      { name: "LP Barbara Luguterah", photo: "https://customer-assets.emergentagent.com/job_b13f2bc8-8388-405b-8f40-fb57b5adb1bf/artifacts/lb743cu5_IMG_2589.jpeg" }
    ]
  },
  englishTeachers: {
    role: "English Lay School Teachers",
    persons: [
      { name: "Rev. George Obeng", photo: "https://customer-assets.emergentagent.com/job_abb71068-83c4-4eaa-b32c-e6340d4f14af/artifacts/jfktklpx_IMG_1864.jpeg" },
      { name: "LP Lydia Boateng", photo: "https://customer-assets.emergentagent.com/job_72ece9e7-e94e-4944-ab3d-b8ae683d264f/artifacts/vapwv2hz_IMG_1852.jpeg" },
      { name: "Rev. Caradoc Nyarko", photo: "https://customer-assets.emergentagent.com/job_a4f99821-48b3-4036-914e-9159037c1b8b/artifacts/kbgsk4xn_carnew.jpeg" },
      { name: "Ps. Solomon Arko-Mensah", photo: "https://customer-assets.emergentagent.com/job_72ece9e7-e94e-4944-ab3d-b8ae683d264f/artifacts/k02wy7mc_IMG_1859.jpeg" },
      { name: "Ps. Kwame Addai-Antwi", photo: "https://customer-assets.emergentagent.com/job_72ece9e7-e94e-4944-ab3d-b8ae683d264f/artifacts/bcrhf8rz_IMG_1861.jpeg" },
      { name: "LP Shirley Appiah", photo: "https://customer-assets.emergentagent.com/job_abb71068-83c4-4eaa-b32c-e6340d4f14af/artifacts/vfdjde1m_IMG_1865.jpeg" },
      { name: "Rev. Davis Amofa", photo: "https://customer-assets.emergentagent.com/job_5852d7c3-cbea-4b2e-8f5e-48df2bdbd22c/artifacts/431l0r6r_IMG_2586.jpeg" },
      { name: "LP Francisca Boakye", photo: "https://customer-assets.emergentagent.com/job_5852d7c3-cbea-4b2e-8f5e-48df2bdbd22c/artifacts/065fz04r_IMG_2587.jpeg" },
      { name: "LP Meloney Moses", photo: "https://customer-assets.emergentagent.com/job_b13f2bc8-8388-405b-8f40-fb57b5adb1bf/artifacts/tyo86vh0_IMG_2591.jpeg" },
      { name: "LP Maame Efua Christain", photo: "https://customer-assets.emergentagent.com/job_a4f99821-48b3-4036-914e-9159037c1b8b/artifacts/93s2zvru_1mfua.jpeg" }
    ]
  },
  frenchTeachers: {
    role: "French Lay School Teachers",
    persons: [
      { name: "LP Arlette Mbarga", photo: "https://customer-assets.emergentagent.com/job_abb71068-83c4-4eaa-b32c-e6340d4f14af/artifacts/nv3i6bg5_IMG_1872.jpeg" }
    ]
  }
};

const OrganizationPage = () => {
  const [expandedRole, setExpandedRole] = useState(null);

  const toggleRole = (role) => {
    setExpandedRole(expandedRole === role ? null : role);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-14">
        {/* Hero Banner */}
        <div className="bg-[#5f7c8a] py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-light text-white tracking-[0.15em]">
              LAIKOS LAY SCHOOLS
            </h1>
            <div className="w-48 h-1 bg-white/50 mx-auto mt-4" />
            <p className="text-xl md:text-2xl text-white/90 mt-6 font-light italic">
              Deepen Your Understanding, Strengthen Your Faith
            </p>
          </div>
        </div>

        {/* Back Link */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#5f7c8a] hover:text-[#4a6470] transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
        </div>

        {/* Organization Chart Section */}
        <section className="max-w-6xl mx-auto px-2 sm:px-4 pb-12">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 md:p-8 pb-64">
            <div className="flex items-center gap-3 mb-6 justify-center">
              <Building2 size={24} className="text-[#5f7c8a]" />
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">Organizational Chart</h2>
            </div>
            
            {/* Org Chart */}
            <div className="flex flex-col items-center mb-16">
              {/* Level 1: Moderator */}
              <div className="flex flex-col items-center">
                <div className="bg-white border-2 border-[#5f7c8a] px-3 py-2 rounded-lg shadow-lg text-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_72ece9e7-e94e-4944-ab3d-b8ae683d264f/artifacts/6ethajol_bce7730c-1a66-4e51-aeaf-073a98c1fcc9.jpeg" 
                    alt="Rev. Justice Hagan"
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#5f7c8a] mx-auto mb-1"
                  />
                  <p className="font-bold text-gray-800 text-xs">Rev. Justice Hagan</p>
                  <p className="text-[#5f7c8a] font-semibold text-[10px] mt-0.5">Moderator</p>
                </div>
                <div className="w-0.5 h-4 bg-gray-300"></div>
              </div>
              
              {/* Level 2: Administrator */}
              <div className="flex flex-col items-center">
                <div className="bg-white border-2 border-[#4a6470] px-3 py-2 rounded-lg shadow-lg text-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_abb71068-83c4-4eaa-b32c-e6340d4f14af/artifacts/fk6kw4i2_IMG_1873.jpeg" 
                    alt="ES. Ivanna Akwele Kiruja"
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#4a6470] mx-auto mb-1"
                  />
                  <p className="font-bold text-gray-800 text-xs">ES. Ivanna Akwele Kiruja</p>
                  <p className="text-[#4a6470] font-semibold text-[10px] mt-0.5">Administrator</p>
                </div>
                <div className="w-0.5 h-4 bg-gray-300"></div>
              </div>
              
              {/* Level 3: IT Admin and Teachers */}
              {/* Desktop: Show horizontal connector line with T-junction */}
              <div className="hidden sm:flex flex-col items-center w-full">
                {/* T-Junction Structure */}
                <div className="relative" style={{width: '480px', height: '120px'}}>
                  {/* The horizontal line */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-0.5 bg-gray-300"></div>
                  
                  {/* Left vertical drop - positioned at left end of horizontal */}
                  <div className="absolute top-0 left-[90px] w-0.5 h-6 bg-gray-300"></div>
                  
                  {/* Right vertical drop - positioned at right end of horizontal */}
                  <div className="absolute top-0 right-[89px] w-0.5 h-6 bg-gray-300"></div>
                  
                  {/* IT Admin Box - centered under left vertical */}
                  <div className="absolute top-6 left-[90px] -translate-x-1/2">
                    <div className="bg-white border-2 border-blue-500 px-3 py-2 rounded-lg shadow-lg text-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_72ece9e7-e94e-4944-ab3d-b8ae683d264f/artifacts/xoon94vv_Screenshot_20240430-230834-199_Original.png" 
                        alt="Rev. Ernest Attafuah"
                        className="w-10 h-10 rounded-full object-cover border-2 border-blue-500 mx-auto mb-1"
                      />
                      <p className="font-bold text-gray-800 text-[10px]">Rev. Ernest Attafuah</p>
                      <p className="text-blue-500 font-semibold text-[9px] mt-0.5">IT Admin</p>
                    </div>
                  </div>
                  
                  {/* Teachers Box - centered under right vertical */}
                  <div className="absolute top-6 right-[89px] translate-x-1/2">
                    <div className="flex flex-col items-center">
                      <div className="bg-white border-2 border-green-500 px-3 py-2 rounded-lg shadow-lg text-center">
                        <p className="font-bold text-green-600 text-[10px] mb-1">Teachers</p>
                        <div className="flex gap-2 flex-wrap justify-center">
                          <div className="flex flex-col items-center">
                            <img src="https://customer-assets.emergentagent.com/job_72ece9e7-e94e-4944-ab3d-b8ae683d264f/artifacts/hivh7eoc_IMG_1860.jpeg" alt="LP. Elaine Osei" className="w-8 h-8 rounded-full object-cover border-2 border-green-500 mb-0.5"/>
                            <p className="font-bold text-gray-800 text-[8px]">LP. Elaine Osei</p>
                          </div>
                          <div className="flex flex-col items-center">
                            <img src="https://customer-assets.emergentagent.com/job_abb71068-83c4-4eaa-b32c-e6340d4f14af/artifacts/xgtzrx0e_IMG_1866.jpeg" alt="LP. Yvonne Fleischer" className="w-8 h-8 rounded-full object-cover border-2 border-green-500 mb-0.5"/>
                            <p className="font-bold text-gray-800 text-[8px]">LP. Yvonne Fleischer</p>
                          </div>
                          <div className="flex flex-col items-center">
                            <img src="https://customer-assets.emergentagent.com/job_abb71068-83c4-4eaa-b32c-e6340d4f14af/artifacts/4jpz0ydt_IMG_1869.jpeg" alt="LP. Emma Mefful" className="w-8 h-8 rounded-full object-cover border-2 border-green-500 mb-0.5"/>
                            <p className="font-bold text-gray-800 text-[8px]">LP. Emma Mefful</p>
                          </div>
                          <div className="flex flex-col items-center">
                            <img src="https://customer-assets.emergentagent.com/job_b13f2bc8-8388-405b-8f40-fb57b5adb1bf/artifacts/lb743cu5_IMG_2589.jpeg" alt="LP. Barbara Luguterah" className="w-8 h-8 rounded-full object-cover border-2 border-green-500 mb-0.5"/>
                            <p className="font-bold text-gray-800 text-[8px]">LP. Barbara Luguterah</p>
                          </div>
                        </div>
                      </div>
                      {/* Vertical line going DOWN from Teachers to connect to English/French T-junction */}
                      <div className="w-0.5 h-4 bg-gray-300"></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Mobile: Stack vertically */}
              <div className="flex sm:hidden flex-col items-center gap-4">
                {/* IT Admin */}
                <div className="flex flex-col items-center">
                  <div className="w-0.5 h-4 bg-gray-300"></div>
                  <div className="bg-white border-2 border-blue-500 px-4 py-3 rounded-lg shadow-lg text-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_72ece9e7-e94e-4944-ab3d-b8ae683d264f/artifacts/xoon94vv_Screenshot_20240430-230834-199_Original.png" 
                      alt="Rev. Ernest Attafuah"
                      className="w-12 h-12 rounded-full object-cover border-2 border-blue-500 mx-auto mb-1"
                    />
                    <p className="font-bold text-gray-800 text-xs">Rev. Ernest Attafuah</p>
                    <p className="text-blue-500 font-semibold text-[10px] mt-1">IT Admin</p>
                  </div>
                </div>
                
                {/* Teachers */}
                <div className="flex flex-col items-center">
                  <div className="w-0.5 h-4 bg-gray-300"></div>
                  <div className="bg-white border-2 border-green-500 px-4 py-3 rounded-lg shadow-lg text-center">
                    <p className="font-bold text-green-600 text-xs mb-2">Teachers</p>
                    <div className="flex gap-3 flex-wrap justify-center">
                      <div className="flex flex-col items-center">
                        <img src="https://customer-assets.emergentagent.com/job_72ece9e7-e94e-4944-ab3d-b8ae683d264f/artifacts/hivh7eoc_IMG_1860.jpeg" alt="LP. Elaine Osei" className="w-10 h-10 rounded-full object-cover border-2 border-green-500 mb-1"/>
                        <p className="font-bold text-gray-800 text-[10px]">LP. Elaine Osei</p>
                      </div>
                      <div className="flex flex-col items-center">
                        <img src="https://customer-assets.emergentagent.com/job_abb71068-83c4-4eaa-b32c-e6340d4f14af/artifacts/xgtzrx0e_IMG_1866.jpeg" alt="LP. Yvonne Fleischer" className="w-10 h-10 rounded-full object-cover border-2 border-green-500 mb-1"/>
                        <p className="font-bold text-gray-800 text-[10px]">LP. Yvonne Fleischer</p>
                      </div>
                      <div className="flex flex-col items-center">
                        <img src="https://customer-assets.emergentagent.com/job_abb71068-83c4-4eaa-b32c-e6340d4f14af/artifacts/4jpz0ydt_IMG_1869.jpeg" alt="LP. Emma Mefful" className="w-10 h-10 rounded-full object-cover border-2 border-green-500 mb-1"/>
                        <p className="font-bold text-gray-800 text-[10px]">LP. Emma Mefful</p>
                      </div>
                      <div className="flex flex-col items-center">
                        <img src="https://customer-assets.emergentagent.com/job_b13f2bc8-8388-405b-8f40-fb57b5adb1bf/artifacts/lb743cu5_IMG_2589.jpeg" alt="LP. Barbara Luguterah" className="w-10 h-10 rounded-full object-cover border-2 border-green-500 mb-1"/>
                        <p className="font-bold text-gray-800 text-[10px]">LP. Barbara Luguterah</p>
                      </div>
                    </div>
                  </div>
                  <div className="w-0.5 h-4 bg-gray-300"></div>
                </div>
              </div>
              
              {/* Level 4: English and French Lay School Teachers */}
              {/* Desktop: Show horizontal connector line with T-junction */}
              <div className="hidden sm:flex flex-col items-center w-full">
                {/* T-Junction Structure for English/French Teachers - shifted right to align under Teachers */}
                <div className="relative ml-[280px]" style={{width: '250px', height: '200px'}}>
                  {/* The horizontal line */}
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gray-300"></div>
                  
                  {/* Left vertical drop */}
                  <div className="absolute top-0 left-0 w-0.5 h-10 bg-gray-300"></div>
                  
                  {/* Right vertical drop */}
                  <div className="absolute top-0 right-0 w-0.5 h-10 bg-gray-300"></div>
                  
                  {/* English Teachers Box - under left vertical */}
                  <div className="absolute top-10 left-0 -translate-x-1/2">
                    <div className="bg-white border-2 border-orange-500 px-2 py-2 rounded-lg shadow-lg text-center">
                      <p className="font-bold text-orange-600 text-[10px] mb-1">English Lay School Teachers</p>
                      <div className="grid grid-cols-2 gap-1">
                        {orgPersonnel.englishTeachers.persons.map((person, index) => (
                          <div key={index} className="flex items-center gap-1 py-0.5">
                            {person.photo ? (
                              <img src={person.photo} alt={person.name} className="w-6 h-6 rounded-full object-cover border border-orange-400"/>
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-orange-200 flex items-center justify-center border border-orange-400">
                                <span className="text-orange-600 font-bold text-[6px]">{person.name.split(' ').map(n => n[0]).join('')}</span>
                              </div>
                            )}
                            <p className="text-black font-bold text-[8px] text-left">{person.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* French Teachers Box - under right vertical */}
                  <div className="absolute top-10 right-0 translate-x-1/2">
                    <div className="bg-white border-2 border-purple-500 px-2 py-2 rounded-lg shadow-lg text-center">
                      <p className="font-bold text-purple-600 text-[10px] mb-1">French Lay School Teachers</p>
                      <div className="flex flex-wrap justify-center gap-1">
                        {orgPersonnel.frenchTeachers.persons.map((person, index) => (
                          <div key={index} className="flex items-center gap-1 py-0.5">
                            {person.photo ? (
                              <img src={person.photo} alt={person.name} className="w-6 h-6 rounded-full object-cover border border-purple-400"/>
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center border border-purple-400">
                                <span className="text-purple-600 font-bold text-[6px]">{person.name.split(' ').map(n => n[0]).join('')}</span>
                              </div>
                            )}
                            <p className="text-black font-bold text-[8px] text-left">{person.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Mobile: English and French Teachers stacked */}
              <div className="flex sm:hidden flex-col items-center gap-4">
                {/* English Teachers */}
                <div className="flex flex-col items-center">
                  <div className="bg-white border-2 border-orange-500 px-3 py-3 rounded-lg shadow-lg text-center">
                    <p className="font-bold text-orange-600 text-xs mb-2">English Lay School Teachers</p>
                    <div className="grid grid-cols-2 gap-2">
                      {orgPersonnel.englishTeachers.persons.map((person, index) => (
                        <div key={index} className="flex items-center gap-1 py-1">
                          {person.photo ? (
                            <img src={person.photo} alt={person.name} className="w-7 h-7 rounded-full object-cover border-2 border-orange-400"/>
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-orange-200 flex items-center justify-center border-2 border-orange-400">
                              <span className="text-orange-600 font-bold text-[7px]">{person.name.split(' ').map(n => n[0]).join('')}</span>
                            </div>
                          )}
                          <p className="text-black font-bold text-[9px] text-left">{person.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="w-0.5 h-4 bg-gray-300"></div>
                </div>
                
                {/* French Teachers */}
                <div className="flex flex-col items-center">
                  <div className="bg-white border-2 border-purple-500 px-3 py-3 rounded-lg shadow-lg text-center">
                    <p className="font-bold text-purple-600 text-xs mb-2">French Lay School Teachers</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {orgPersonnel.frenchTeachers.persons.map((person, index) => (
                        <div key={index} className="flex items-center gap-1 py-1">
                          {person.photo ? (
                            <img src={person.photo} alt={person.name} className="w-7 h-7 rounded-full object-cover border-2 border-purple-400"/>
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-purple-200 flex items-center justify-center border-2 border-purple-400">
                              <span className="text-purple-600 font-bold text-[7px]">{person.name.split(' ').map(n => n[0]).join('')}</span>
                            </div>
                          )}
                          <p className="text-black font-bold text-[9px] text-left">{person.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="max-w-4xl mx-auto px-3 sm:px-4 pb-8 sm:pb-12">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="bg-[#5f7c8a] w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0">
                <BookOpen size={20} className="text-white sm:hidden" />
                <BookOpen size={24} className="text-white hidden sm:block" />
              </div>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">Deepen Your Understanding of God&apos;s Word</h2>
            </div>
            
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6">
              The Laikos Lay Schools are part of the <strong>&apos;Understanding Campaign,&apos;</strong> helping ordinary Christians to deepen their understanding of the Word so they remain rooted and grounded in their faith.
            </p>
            
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
              The Understanding Campaign prevents the sheep from being snatched away by the wicked one. Our aim is to feed the flock in such a way that they grow in knowledge and understanding.
            </p>
          </div>
        </section>

        {/* Scriptural Basis */}
        <section className="bg-[#f8f9fa] py-8 sm:py-12 px-3 sm:px-4 mb-8 sm:mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 md:p-8">
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                <Quote size={32} className="text-[#5f7c8a] flex-shrink-0 sm:mt-2 hidden sm:block" />
                <Quote size={24} className="text-[#5f7c8a] flex-shrink-0 sm:hidden" />
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                    <span>Scriptural Basis</span>
                  </h3>
                  <blockquote className="text-sm sm:text-base md:text-lg text-gray-700 italic border-l-4 border-[#5f7c8a] pl-3 sm:pl-4 mb-3 sm:mb-4">
                    &quot;When any one heareth the word of the kingdom, and understandeth it not, then cometh the wicked one, and catcheth away that which was sown in his heart.&quot;
                  </blockquote>
                  <p className="text-[#5f7c8a] font-semibold text-right text-sm sm:text-base">— Matthew 13:19</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Sections Grid */}
        <section className="max-w-6xl mx-auto px-3 sm:px-4 pb-8 sm:pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
            {/* Goal of the School */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-500 w-12 h-12 rounded-full flex items-center justify-center">
                    <Target size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Goal of the School</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  The Understanding Campaign prevents the sheep from being snatched away by the wicked one. Our aim is to feed the flock in a way that deepens knowledge and understanding.
                </p>
              </CardContent>
            </Card>

            {/* Why Understanding Matters */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-500 w-12 h-12 rounded-full flex items-center justify-center">
                    <Lightbulb size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Why Understanding Matters</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Understanding the Word determines the fruitfulness and usefulness of a Christian. Great understanding leads to fruitfulness; little understanding leads to barrenness.
                </p>
              </CardContent>
            </Card>

            {/* Registration Path */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-purple-500 w-12 h-12 rounded-full flex items-center justify-center">
                    <Users size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Registration Path</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Students can register using the registration form on our website. Once registration is complete, they will receive access to the full student dashboard where they can enroll in schools and take exams.
                </p>
              </CardContent>
            </Card>

            {/* Discover the Schools */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-orange-500 w-12 h-12 rounded-full flex items-center justify-center">
                    <Sprout size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Discover the Schools</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Learn more about the Laikos Lay Schools – how classes work, what students experience, and how you can get started on your journey of deeper understanding.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* The Lay Schools Image Section */}
        <section className="max-w-7xl mx-auto px-4 pb-12">
          <div className="bg-red-600 rounded-lg shadow-md overflow-hidden p-6 md:p-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_abb71068-83c4-4eaa-b32c-e6340d4f14af/artifacts/omcyge1x_creativefabrica-ai-image-editor-1-bot%20%281%29.png" 
              alt="The Lay Schools - Overview of all schools and their curriculum"
              className="w-full h-auto rounded-lg"
            />
          </div>
        </section>

        {/* Our Values Section */}
        <section className="bg-white py-12 px-4 mb-12">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">Our Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="text-center hover:shadow-lg transition-shadow border-t-4 border-[#5f7c8a]">
                <CardContent className="pt-6">
                  <div className="bg-[#5f7c8a] w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen size={24} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Scripture-Based Learning</h3>
                  <p className="text-sm text-gray-600">All our courses are rooted in the timeless truths of God&apos;s Word, helping you grow in faith and understanding.</p>
                </CardContent>
              </Card>
              
              <Card className="text-center hover:shadow-lg transition-shadow border-t-4 border-green-500">
                <CardContent className="pt-6">
                  <div className="bg-green-500 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart size={24} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Community & Support</h3>
                  <p className="text-sm text-gray-600">Building a supportive community of learners growing together in faith and understanding.</p>
                </CardContent>
              </Card>
              
              <Card className="text-center hover:shadow-lg transition-shadow border-t-4 border-blue-500">
                <CardContent className="pt-6">
                  <div className="bg-blue-500 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users size={24} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Accessible to All</h3>
                  <p className="text-sm text-gray-600">Making quality Christian education available to everyone, everywhere, regardless of background.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="bg-[#5f7c8a] py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Contact / Questions</h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              For questions or support, please reach out to us. We&apos;re here to help you on your journey of faith and understanding.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="tel:+16787633554"
                className="inline-flex items-center gap-2 bg-white text-[#5f7c8a] px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                <Phone size={20} />
                ES Ivanna: +1 (678) 763-3554
              </a>
              <a 
                href="mailto:contact@laikosschools.org"
                className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                <Mail size={20} />
                Contact Your Local Pastor
              </a>
            </div>
          </div>
        </section>

        {/* Registration Countdown Timer Section - Using Reusable Component with Admin */}
        <AdminCountdownTimer showAdminButton={true} />
      </main>

      <Footer />
    </div>
  );
};

export default OrganizationPage;
